import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { LandingLocale, LandingT } from '../../i18n/landing'
import { callDemoExtract, type DemoWord } from '../../api/demo'
import { DemoResultList } from './DemoResultList'
import { trackEvent } from '../../utils/analytics'

const MAX_CHARS = 200

// The demo translates into the visitor's language; an English visitor gets
// the words alone, as the source language is English.
function demoSourceLanguage(locale: LandingLocale): string | undefined {
  return locale === 'en' ? undefined : locale
}

// Static example words shown in the empty state before first submission, with
// their translations from the visitor's interface table (none in English).
const PREVIEW_WORDS: DemoWord[] = [
  { lemma: 'resilience', pos: 'NOUN', cefr_level: 'B2' },
  { lemma: 'curious', pos: 'ADJ', cefr_level: 'A2' },
  { lemma: 'phenomenon', pos: 'NOUN', cefr_level: 'C1' },
  { lemma: 'elaborate', pos: 'VERB', cefr_level: 'B2' },
]

function previewWords(translations: Record<string, string>): DemoWord[] {
  return PREVIEW_WORDS.map((w) =>
    translations[w.lemma] ? { ...w, translation: translations[w.lemma] } : w,
  )
}

type DemoState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'done'; detectedLanguage: string; words: DemoWord[] }
  | { status: 'error'; message: string }

interface DemoWidgetProps {
  locale: LandingLocale
  t: LandingT
}

export function DemoWidget({ locale, t }: DemoWidgetProps) {
  const [text, setText] = useState('')
  const [demoState, setDemoState] = useState<DemoState>({ status: 'idle' })
  const demo = t.demo

  const overLimit = text.length > MAX_CHARS
  const isLoading = demoState.status === 'loading'
  const submitDisabled = !text.trim() || overLimit || isLoading

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (submitDisabled) return

    setDemoState({ status: 'loading' })
    trackEvent('landing_demo_used')

    try {
      const res = await callDemoExtract(text, demoSourceLanguage(locale))
      setDemoState({
        status: 'done',
        detectedLanguage: res.detected_language,
        words: res.words,
      })
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code ?? ''
      let message = demo.errors.unknown
      if (code === 'TEXT_TOO_LONG') message = demo.errors.tooLong
      else if (code === 'RATE_LIMITED') message = demo.errors.rateLimit
      else if (code === 'UNSUPPORTED_LANGUAGE') message = demo.errors.unsupportedLanguage
      setDemoState({ status: 'error', message })
    }
  }

  const preview = previewWords(demo.previewTranslations)

  return (
    <div
      data-demo-section
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 16,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Widget header */}
      <div
        style={{
          padding: '14px 20px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--accent-soft)',
        }}
      >
        <p
          style={{
            margin: 0,
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--accent)',
            letterSpacing: '0.01em',
            fontFamily: 'var(--font-ui)',
          }}
        >
          {demo.title}
        </p>
      </div>

      {/* Input form */}
      <form onSubmit={handleSubmit} style={{ padding: '16px 20px 12px' }}>
        <textarea
          data-demo-input
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder={demo.placeholder}
          rows={4}
          style={{
            width: '100%',
            resize: 'none',
            background: 'var(--bg-secondary)',
            border: `1.5px solid ${overLimit ? 'var(--danger)' : 'var(--border)'}`,
            borderRadius: 8,
            padding: '10px 12px',
            fontSize: 14,
            fontFamily: 'var(--font-ui)',
            color: 'var(--text-primary)',
            outline: 'none',
            boxSizing: 'border-box',
            lineHeight: 1.6,
            transition: 'border-color 0.15s',
            display: 'block',
          }}
          onFocus={e => {
            if (!overLimit) e.currentTarget.style.borderColor = 'var(--accent)'
          }}
          onBlur={e => {
            e.currentTarget.style.borderColor = overLimit ? 'var(--danger)' : 'var(--border)'
          }}
        />

        {/* Character counter */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: 6,
            marginBottom: 12,
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontFamily: 'var(--font-ui)',
              fontVariantNumeric: 'tabular-nums',
              color: overLimit ? 'var(--danger)' : 'var(--text-secondary)',
              transition: 'color 0.15s',
            }}
          >
            {overLimit ? demo.charLimit : `${text.length} / ${MAX_CHARS}`}
          </span>
        </div>

        <button
          type="submit"
          disabled={submitDisabled}
          style={{
            width: '100%',
            background: submitDisabled ? 'var(--accent-soft)' : 'var(--accent-fill)',
            color: submitDisabled ? 'var(--accent)' : 'var(--text-on-accent)',
            border: 'none',
            borderRadius: 8,
            padding: '10px 16px',
            fontSize: 14,
            fontWeight: 600,
            fontFamily: 'var(--font-ui)',
            cursor: submitDisabled ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            transition: 'background 0.15s, color 0.15s',
            minHeight: 40,
          }}
        >
          {isLoading ? (
            <>
              <span
                className="demo-spinner"
                style={{
                  display: 'inline-block',
                  width: 14,
                  height: 14,
                  border: '2px solid currentColor',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                }}
              />
              {demo.loading}
            </>
          ) : (
            demo.submit
          )}
        </button>
      </form>

      {/* Results area */}
      <div style={{ padding: '0 20px 20px', flex: 1 }}>
        {/* Idle: show static preview */}
        {demoState.status === 'idle' && (
          <div>
            <p
              style={{
                margin: '0 0 8px',
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'var(--text-secondary)',
                fontFamily: 'var(--font-ui)',
              }}
            >
              {demo.emptyCaption}
            </p>
            <DemoResultList
              words={preview}
              detectedLanguage="en"
              isPreview
            />
          </div>
        )}

        {/* Error */}
        {demoState.status === 'error' && (
          <div
            role="alert"
            style={{
              padding: '11px 14px',
              background: 'var(--danger-soft)',
              border: '1px solid color-mix(in srgb, var(--danger) 20%, transparent)',
              borderRadius: 8,
              fontSize: 13,
              color: 'var(--danger)',
              lineHeight: 1.5,
              fontFamily: 'var(--font-ui)',
            }}
          >
            {demoState.message}
          </div>
        )}

        {/* Loading skeleton */}
        {demoState.status === 'loading' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 4 }}>
            {[80, 65, 75, 55].map((w, i) => (
              <div
                key={i}
                style={{
                  height: 14,
                  borderRadius: 7,
                  background: 'var(--border)',
                  width: `${w}%`,
                  opacity: 0.6,
                }}
                className="demo-skeleton-pulse"
              />
            ))}
          </div>
        )}

        {/* Results */}
        {demoState.status === 'done' && (
          <div>
            {demoState.words.length > 0 && (
              <p
                style={{
                  margin: '0 0 8px',
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'var(--text-secondary)',
                  fontFamily: 'var(--font-ui)',
                }}
              >
                {demoState.detectedLanguage !== 'unknown'
                  ? `${demoState.words.length} ${demo.wordsFound}`
                  : demo.noLanguage}
              </p>
            )}

            <DemoResultList
              words={demoState.words}
              detectedLanguage={demoState.detectedLanguage}
            />

            {demoState.words.length > 0 && (
              <Link
                to={`/register?lang=${demoState.detectedLanguage}`}
                style={{
                  display: 'block',
                  textAlign: 'center',
                  background: 'var(--accent-fill)',
                  color: 'var(--text-on-accent)',
                  textDecoration: 'none',
                  fontSize: 14,
                  fontWeight: 600,
                  fontFamily: 'var(--font-ui)',
                  padding: '12px 20px',
                  borderRadius: 8,
                  marginTop: 16,
                  transition: 'background 0.15s, transform 0.1s',
                }}
                onMouseEnter={e => {
                  const el = e.currentTarget as HTMLAnchorElement
                  el.style.background = 'var(--accent-hover)'
                  el.style.transform = 'translateY(-1px)'
                }}
                onMouseLeave={e => {
                  const el = e.currentTarget as HTMLAnchorElement
                  el.style.background = 'var(--accent-fill)'
                  el.style.transform = 'translateY(0)'
                }}
              >
                {demo.ctaResult}
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
